import { useState, useRef, useEffect } from "react";
const SUPPORT_EVENT_TYPES = [
  "data-events-connected",
  "data-live-takeover-state",
  "data-human-reply",
  "data-live-takeover-started",
  "data-live-takeover-ended",
  "data-takeover-team-changed",
  "data-ticket-status-updated",
  "data-user-message-mirrored",
  "data-user-message-delivery-failed",
  "data-debug-ping"
];
const DEFAULT_BASE_URL = "";
const READY_STATE_LABEL = {
  0: "CONNECTING",
  1: "OPEN",
  2: "CLOSED"
};
function useConciergeSupportEvents(conversationId, apiBaseUrl, onReconnect) {
  const [liveParts, setLiveParts] = useState([]);
  const onReconnectRef = useRef(onReconnect);
  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);
  useEffect(() => {
    if (!conversationId) {
      setLiveParts([]);
      console.info("[concierge-events] skip_open no_conversation_id");
      return;
    }
    const baseUrl = apiBaseUrl ?? DEFAULT_BASE_URL;
    const url = `${baseUrl}/api-rewards/concierge/support/conversation/${encodeURIComponent(
      conversationId
    )}/events`;
    let es = null;
    const openedAt = Date.now();
    let lastReadyState;
    let readyStatePoll = null;
    try {
      es = new EventSource(url, { withCredentials: true });
      console.info("[concierge-events] event_source_created", {
        conversationId,
        url,
        readyState: READY_STATE_LABEL[es.readyState] ?? es.readyState
      });
    } catch (err) {
      console.warn("[concierge-events] event_source_create_failed", { conversationId, url, err });
      return;
    }
    readyStatePoll = setInterval(() => {
      const rs = es == null ? void 0 : es.readyState;
      if (rs !== void 0 && rs !== lastReadyState) {
        const fromLabel = lastReadyState === void 0 ? "INIT" : READY_STATE_LABEL[lastReadyState] ?? lastReadyState;
        const toLabel = READY_STATE_LABEL[rs] ?? rs;
        console.info("[concierge-events] ready_state_changed", {
          conversationId,
          from: fromLabel,
          to: toLabel,
          uptimeMs: Date.now() - openedAt
        });
        lastReadyState = rs;
      }
    }, 1e3);
    es.onerror = (errEvent) => {
      console.warn("[concierge-events] error", {
        conversationId,
        readyState: es ? READY_STATE_LABEL[es.readyState] ?? es.readyState : "NULL",
        uptimeMs: Date.now() - openedAt,
        // EventSource error events don't carry useful payload — readyState
        // is the only signal worth logging. CONNECTING after error means
        // browser is auto-reconnecting; CLOSED means it gave up.
        errEvent
      });
    };
    let connectCount = 0;
    const listeners = [];
    for (const type of SUPPORT_EVENT_TYPES) {
      const fn = (msg) => {
        var _a;
        let data;
        try {
          data = msg.data ? JSON.parse(msg.data) : void 0;
        } catch (err) {
          console.warn("[concierge-events] event_parse_failed", { conversationId, type, raw: msg.data, err });
          return;
        }
        console.info("[concierge-events] event_received", {
          conversationId,
          type,
          data,
          uptimeMs: Date.now() - openedAt
        });
        if (type === "data-debug-ping") {
          return;
        }
        setLiveParts((prev) => [...prev, { type, data }]);
        if (type === "data-events-connected") {
          connectCount += 1;
          console.info("[concierge-events] connected", { conversationId, connectCount });
          if (connectCount >= 2) {
            console.info("[concierge-events] reconnected firing_onReconnect", {
              conversationId,
              connectCount
            });
            try {
              (_a = onReconnectRef.current) == null ? void 0 : _a.call(onReconnectRef);
            } catch (err) {
              console.warn("[concierge-events] onReconnect_handler_threw", { conversationId, err });
            }
          }
        }
      };
      es.addEventListener(type, fn);
      listeners.push({ type, fn });
    }
    return () => {
      console.info("[concierge-events] closing", {
        conversationId,
        uptimeMs: Date.now() - openedAt
      });
      if (readyStatePoll) clearInterval(readyStatePoll);
      for (const { type, fn } of listeners) {
        es == null ? void 0 : es.removeEventListener(type, fn);
      }
      es == null ? void 0 : es.close();
    };
  }, [conversationId, apiBaseUrl]);
  return liveParts;
}
export {
  SUPPORT_EVENT_TYPES,
  useConciergeSupportEvents
};
//# sourceMappingURL=useConciergeSupportEvents.js.map
